import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { createSelector } from 'reselect'
import { pathToRegexp } from 'path-to-regexp'
import cx from 'classnames'

import { Drawer, useTheme, makeStyles } from '@material-ui/core'

import useViewportSize from '~/components/useViewportSize'
import LoadingEcommerce from './LoadingEcommerce'
import RenderEcommerce from './RenderEcommerce'
import EcommerceNotFound from './EcommerceNotFound'
import { useDataEcommerce } from './useDataEcommerce'

import { selectChart } from '~/selectors'
import actions from '~/actions'
import api from '~/api'

const selector = createSelector(
  (state) => state.app,
  (...args) => args
)

const useStyles = makeStyles((theme) => ({
  paper: {
    position: 'absolute',
    height: '100%',
    border: 0
  },
  drawerMobile: {
    width: '100%',
    height: 'auto',
    overflow: 'hidden',
    flexShrink: 0
  },
  drawerDesktop: {
    width: theme.gofind.asideWidth,
    height: 'auto',
    overflow: 'hidden',
    flexShrink: 0
  },
  blurOnLoad: { filter: 'blur(5px)' }
}))

export default function DrawerEcommerce({
  openEcommerce,
  setOpenEcommerce,
  setApiEcommerce,
  dataEcommerce,
  loadingEcommerce,
  config
}) {
  const [app] = useSelector(selector, shallowEqual)
  const [chart] = useSelector(selectChart, shallowEqual)

  const { isEcommerceNotFound, isEnableAlertPrice } = useDataEcommerce(dataEcommerce, config)

  const mounted = useRef(false)

  const classes = useStyles()
  const theme = useTheme()
  const history = useHistory()
  const viewport = useViewportSize()
  const dispatch = useDispatch()
  const isMobile = viewport.width < theme.gofind.desktopMaxWidth

  const { currentProduct, stores, view } = app
  const { period } = chart

  const urlRegex = pathToRegexp('/produto/:gtin/detalhes', null, { sensitive: true })

  const [isRouteProductDetails, setIsRouteProductDetails] = useState(false)

  useEffect(() => {
    setIsRouteProductDetails(urlRegex.test(history.location.pathname))
  }, [history.location.pathname])

  const handleClose = () => {
    const isUrlRegex = urlRegex.test(/produto(?:([\/\w\-]+))[\d]+/g) || []

    dispatch(actions.dialog.error(false))
    dispatch(actions.dialog.reset())
    dispatch(actions.chart.reset())

    setOpenEcommerce(null)

    if (config.showContextViewStore) {
      if (currentProduct?.gtin !== 'all' || isUrlRegex) return

      history.push('/produto/todos')
      return
    }

    if (view === 'VIEW_STORES' && (!stores || !stores.length)) history.push('/')
    if (view === 'VIEW_PRODUCTS' && (!stores || !stores.length)) history.push('/')
  }

  useEffect(() => {
    mounted.current = true
    return () => (mounted.current = false)
  }, [])

  useEffect(() => {
    ;(async () => {
      if (!mounted.current || !isEnableAlertPrice || !openEcommerce) return

      try {
        dispatch(actions.chart.load(true))
        const insights = await api.ecommerceHistoryInsights(openEcommerce.gtin, period)

        if (Array.isArray(insights)) {
          dispatch(actions.chart.data(insights))
          return
        }

        dispatch(actions.chart.data([]))
      } catch (err) {
        console.log('TCL: ~ getHistoryInsights ~ err', err)
      } finally {
        dispatch(actions.chart.load(false))
      }
    })()
  }, [openEcommerce, period, isEnableAlertPrice])

  const isOpenedDrawer =
    (isRouteProductDetails && loadingEcommerce) ||
    (isRouteProductDetails &&
      Boolean(!isEcommerceNotFound) &&
      Boolean(openEcommerce) &&
      Boolean(!loadingEcommerce))

  return (
    <>
      <LoadingEcommerce isLoading={loadingEcommerce} />

      {isEcommerceNotFound && openEcommerce && !loadingEcommerce && (
        <EcommerceNotFound product={openEcommerce} onClose={handleClose} />
      )}

      <Drawer
        hideBackdrop
        anchor="left"
        id="drawer-ecommerce-google"
        open={isOpenedDrawer}
        onClose={handleClose}
        classes={{
          paper: cx(classes.paper, {
            [classes.drawerMobile]: isMobile,
            [classes.drawerDesktop]: !isMobile,
            [classes.blurOnLoad]: loadingEcommerce
          })
        }}
        PaperProps={{ 'data-cy': 'drawer-ecommerce-google' }}
        ModalProps={{
          container: document.getElementById('gofind-finder') || document.body,
          style: { position: 'absolute', width: isMobile ? '100%' : theme.gofind.asideWidth }
        }}
      >
        <RenderEcommerce
          openEcommerce={openEcommerce}
          loadingEcommerce={loadingEcommerce}
          dataEcommerce={dataEcommerce}
          configEcommerce={config}
          setOpenEcommerce={setOpenEcommerce}
          setApiEcommerce={setApiEcommerce}
          handleClose={handleClose}
        />
      </Drawer>
    </>
  )
}
